import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "./PageHeader";
import { generationEta } from "./genEta";
import PruefGates from "./PruefGates";
import {
  FileText,
  Mail,
  Globe,
  FileCode,
  Sparkles,
  RotateCcw,
  Activity,
  Cpu,
  Check,
  Loader,
  Target,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const TYPE_ICONS = { jobs: FileText, emails: Mail, careers: Globe, landings: FileCode };

// Rohe Modell-IDs in lesbare Namen übersetzen.
function modelName(id) {
  if (!id) return "—";
  const s = String(id).toLowerCase();
  if (s.includes("opus")) return "Claude Opus 4.x";
  if (s.includes("sonnet")) return "Claude Sonnet 4.6";
  if (s.includes("haiku-4")) return "Claude Haiku 4.5";
  if (s.includes("haiku")) return "Claude Haiku 3";
  return id;
}

const ROLE_META = {
  analysis: { icon: Target, label: "Analyse" },
  writer: { icon: Sparkles, label: "Writer" },
  review: { icon: Check, label: "Prüfung" },
  rewriter: { icon: RotateCcw, label: "Rewriter" },
  format: { icon: FileText, label: "Format" },
};

const STAGE_LABELS = {
  pending: "In Warteschlange",
  agent1: "Schreiben",
  writing: "Schreiben",
  "agents2-8": "Prüfung",
  reviewing: "Prüfung",
  agent7: "Überarbeitung",
  rewriting: "Überarbeitung",
  "final-check": "Schlussprüfung",
  "hallucination-check": "Faktentreue-Gate",
  "content-check": "Schlussprüfung",
  "content-rewrite": "Feinschliff",
  "zielgruppe-check": "Zielgruppen-Check",
  formatting: "Formatierung",
  done: "Fertig",
};

function stageLabel(stage) {
  if (!stage) return "Läuft …";
  if (STAGE_LABELS[stage]) return STAGE_LABELS[stage];
  const m = String(stage).match(/^(review|rewrite)-(\d+)$/);
  if (m) return `${m[1] === "review" ? "Prüfung" : "Überarbeitung"} · Durchlauf ${m[2]}`;
  const h = String(stage).match(/^hallucination-check-(\d+)$/);
  if (h) return `Faktentreue-Gate · Durchlauf ${h[1]}`;
  const hr = String(stage).match(/^hall-rewrite-(\d+)$/);
  if (hr) return `Faktentreue-Korrektur · Durchlauf ${hr[1]}`;
  // Schlusspruefung laeuft in Runden: content-check-2, zielgruppe-check-2, content-rewrite-2
  const s2 = String(stage).match(/^(content-check|zielgruppe-check|content-rewrite)-(\d+)$/);
  if (s2) return `${STAGE_LABELS[s2[1]] || s2[1]} · Runde ${s2[2]}`;
  return stage;
}

// Ein Pruef-Agent aus dem Review-Loop. Aufklappen zeigt, worauf er wirklich
// achtet - die Punkte stammen aus seinem tatsaechlichen Pruefauftrag.
function PruefAgent({ agent }) {
  const [offen, setOffen] = useState(false);
  const punkte = agent.pruefpunkte || [];
  if (!punkte.length) {
    return (
      <div className="pipeline-agent">
        <div className="pipeline-agent-name">{agent.name}</div>
        <div className="pipeline-agent-desc">{agent.desc}</div>
      </div>
    );
  }
  return (
    <div className={`pipeline-agent pipeline-agent--klickbar ${offen ? "is-offen" : ""}`}
      role="button" tabIndex={0}
      onClick={() => setOffen(!offen)}
      onKeyDown={(e) => { if (e.key === "Enter") setOffen(!offen); }}>
      <div className="pipeline-agent-name">
        {offen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {agent.name}
        <span className="pipeline-agent-zahl">{punkte.length}</span>
      </div>
      <div className="pipeline-agent-desc">{agent.desc}</div>
      {offen && (
        <ul className="pipeline-agent-punkte">
          {punkte.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      )}
    </div>
  );
}

function countAgents(p) {
  return (p.steps || []).reduce((n, s) => n + (s.parallel ? s.parallel.length : 1), 0);
}

function PipelineCard({ pipeline, gates, onGates }) {
  // Erklaerungen sind eingeklappt: wichtig, aber nicht dauerhaft im Weg.
  const [offenerSchritt, setOffenerSchritt] = useState(null);
  const Icon = TYPE_ICONS[pipeline.id] || FileText;
  const planned = pipeline.status !== "active";

  return (
    <div className={"pipeline-card" + (planned ? " is-planned" : "")}>
      <div className="pipeline-card-head">
        <span className="pipeline-card-icon"><Icon size={19} /></span>
        <span className="pipeline-card-title">{pipeline.label}</span>
        <span className={"pipeline-card-badge " + (planned ? "planned" : "active")}>
          {planned ? "Geplant" : "Aktiv"}
        </span>
      </div>

      {planned ? (
        <p className="pipeline-planned-note">
          Für diesen Texttyp ist noch keine Agenten-Pipeline aktiv. Sie folgt in einer
          späteren Version.
        </p>
      ) : (
        <>
          <div className="pipeline-card-meta">
            {pipeline.reviewModel && pipeline.reviewModel !== pipeline.model ? (
              <>
                <span><Sparkles size={13} /> Schreiben: {modelName(pipeline.model)}</span>
                <span><Check size={13} /> Prüfung: {modelName(pipeline.reviewModel)}</span>
              </>
            ) : (
              <span><Cpu size={13} /> Modell: {modelName(pipeline.model)}</span>
            )}
            <span><Sparkles size={13} /> {countAgents(pipeline)} Agenten</span>
            {pipeline.loop && (
              <span><RotateCcw size={13} /> Bis zu {pipeline.maxIterations} Durchläufe</span>
            )}
          </div>

          <div className="pipeline-steps">
            {pipeline.steps.map((step) => {
              const Role = ROLE_META[step.role] || ROLE_META.writer;
              const RoleIcon = Role.icon;
              const stepModel = (step.role === "review" || step.role === "analysis")
                ? (pipeline.reviewModel || pipeline.model)
                : pipeline.model;
              const offen = offenerSchritt === step.order;
              // Ab diesem Schritt laeuft die Kette je Abschnitt erneut.
              const proAbschnitt = pipeline.proAbschnittAb && step.order >= pipeline.proAbschnittAb;
              const ersterProAbschnitt = pipeline.proAbschnittAb === step.order;
              return (
                <React.Fragment key={step.order}>
                {ersterProAbschnitt && (
                  <div className="pipeline-abschnitt-trenner">
                    <RotateCcw size={13} />
                    <span>{pipeline.hinweis}</span>
                  </div>
                )}
                <div className={`pipeline-step ${proAbschnitt ? "pipeline-step--je-abschnitt" : ""}`}>
                  <span className="pipeline-step-num">{step.order}</span>
                  <span className="pipeline-step-role"><RoleIcon size={12} /> {Role.label}</span>
                  {stepModel && (
                    <span className="pipeline-step-model"><Cpu size={11} /> {modelName(stepModel)}</span>
                  )}
                  <div className="pipeline-step-name">
                    {step.name}
                    {proAbschnitt && <span className="pipeline-step-tag">je Abschnitt</span>}
                  </div>
                  {step.desc && (
                    <button className="pipeline-step-desc-toggle"
                      onClick={() => setOffenerSchritt(offen ? null : step.order)}>
                      {offen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                      {offen ? "Erklärung ausblenden" : "Was passiert hier?"}
                    </button>
                  )}
                  {step.desc && offen && <div className="pipeline-step-desc">{step.desc}</div>}
                  {step.parallel && (
                    <>
                      <div className="pipeline-agents">
                        {step.parallel.map((a) => (
                          <PruefAgent key={a.name} agent={a} />
                        ))}
                      </div>
                      <div className="pipeline-parallel-hint">
                        Diese {step.parallel.length} Agenten prüfen gleichzeitig.
                        {step.parallel.some((a) => (a.pruefpunkte || []).length > 0) &&
                          " Klick einen an, um zu sehen, worauf er achtet."}
                        {step.parallelHinweis && <div className="pipeline-parallel-note">{step.parallelHinweis}</div>}
                      </div>
                    </>
                  )}
                  {step.gatesPhase && gates && (
                    <PruefGates
                      gates={gates.filter((g) => g.phase === step.gatesPhase)}
                      phase={step.gatesPhase}
                      titel={step.gatesTitel}
                      onChange={(neue) => onGates([
                        ...gates.filter((g) => g.phase !== step.gatesPhase),
                        ...neue,
                      ])}
                    />
                  )}
                  {step.loopBack && (
                    <div className="pipeline-step-loopback">
                      <RotateCcw size={12} />
                      <span>{step.loopBack.label}</span>
                    </div>
                  )}
                </div>
                </React.Fragment>
              );
            })}
          </div>

          {pipeline.loop && (
            <div className="pipeline-loop">
              <RotateCcw size={14} />
              <span>
                Die Kette läuft nicht stur von oben nach unten: Sobald ein Prüf-Agent
                oder das Faktentreue-Gate etwas findet, geht die Anzeige einen
                Schritt zurück zum Rewriter und wird erneut geprüft — so lange, bis
                kein Punkt mehr offen ist (max. {pipeline.maxIterations} Durchläufe).
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function AgentsPage() {
  const [pipelines, setPipelines] = useState([]);
  const [gates, setGates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/agents", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { setPipelines(d.pipelines || []); setGates(d.gates || []); })
      .catch(() => setError("Agenten konnten nicht geladen werden."))
      .finally(() => setLoading(false));
  }, []);

  const loadJobs = useCallback(() => {
    fetch("/api/generation-jobs", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setJobs(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadJobs();
    const t = setInterval(loadJobs, 4000);
    return () => clearInterval(t);
  }, [loadJobs]);

  return (
    <>
      <PageHeader
        eyebrow="Texte"
        title="KI-Agenten"
        subtitle="Die Agenten-Pipeline hinter jedem Text — in der Reihenfolge, in der sie arbeiten."
      />

      <section className="agents-live">
        <div className="agents-live-head">
          <Activity size={15} />
          <span>Aktuell aktiv</span>
          {jobs.length > 0 && <span className="agents-live-count">{jobs.length}</span>}
        </div>
        {jobs.length === 0 ? (
          <div className="agents-live-empty">
            Gerade arbeitet kein Agent. Sobald ein Text generiert wird, läuft die Pipeline
            automatisch los — und erscheint hier.
          </div>
        ) : (
          <div className="agents-live-list">
            {jobs.map((j) => (
              <div key={j.id} className="agents-live-row">
                <Loader size={14} className="icon-spin" />
                <div className="agents-live-main">
                  <span className="agents-live-title">{j.stellentitel || "Ohne Titel"}</span>
                  <span className="agents-live-sub">{j.unternehmen}</span>
                </div>
                <div className="agents-live-right">
                  <span className="agents-live-stage">{stageLabel(j.stage)}</span>
                  {generationEta(j.created_at, j.avg_duration_sec).text && (
                    <span className="agents-live-eta">{generationEta(j.created_at, j.avg_duration_sec).text}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {error && <div className="empty-state">{error}</div>}
      {loading && !error && (
        <div className="loading-indicator"><div className="spinner" /><span>Laden …</span></div>
      )}

      <div className="agents-pipelines">
        {pipelines.map((p) => (
          <PipelineCard key={p.id} pipeline={p} gates={gates} onGates={setGates} />
        ))}
      </div>
    </>
  );
}
